import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import { useRouter } from "next/router";
import Image from "next/legacy/image";
import Link from "next/link";

import { getAllTeamMemebers, getTeamPageSettings } from "../lib/api";
import { imageBuilder } from "../lib/sanity";
import { teamDepartments, blurDataURL } from "../lib/constants";
import BlockContent from "../components/block-content";
import Meta from "../components/meta";
import useMediaQuery from "../hooks/useMediaQuery";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// eslint-disable-next-line no-unused-vars
const ContactLinks = ({ email, linkedInUrl, bio, onClickBio = () => {} }) => {
  if (!linkedInUrl) return null;

  return (
    <ul role="list" className="flex items-center space-x-4">
      {/* {bio ? (
        <li>
          <a
            onClick={onClickBio}
            className="text-gray-400 cursor-pointer hover:text-gray-500"
          >
            <span className="sr-only">More Information</span>
            <InformationCircleIcon className="w-5 h-5" />
          </a>
        </li>
      ) : null} */}

      {/* {email ? (
        <li>
          <a
            href={`mailto:${email}`}
            className="text-gray-400 hover:text-gray-500 outline-none"
          >
            <span className="sr-only">Email</span>
            <MailIcon className="w-5 h-5" />
          </a>
        </li>
      ) : null} */}

      {linkedInUrl ? (
        <li>
          <a
            target="_blank"
            rel="noreferrer"
            href={linkedInUrl}
            className="text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">LinkedIn</span>
            <svg
              className="w-4 h-4"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                clipRule="evenodd"
              />
            </svg>
          </a>
        </li>
      ) : null}
    </ul>
  );
};

const filterData = (data, department) => {
  if (!data) return [];
  if (department === "all") return data;
  return data.filter((person) => person.departments.includes(department));
};

export default function Team({ data: initialData = [], settings }) {
  const router = useRouter();
  const isPageWide = useMediaQuery("(min-width: 640px)");
  const { query } = router || {};
  const { department = "all", member } = query || {};

  const [open, setOpen] = useState(false);
  const [person, setPerson] = useState({});

  const leaders = settings?.leadership.members.map(({ _ref }) =>
    initialData.find(({ _id }) => _id === _ref)
  );
  const data = filterData(initialData, department);

  const tabs = [
    { name: "All", href: "/team", current: department === "all" },
    ...teamDepartments.map((i) => ({
      ...i,
      name: i.title,
      href: `/team?department=${i.value}`,
      current: department === i.value,
    })),
  ];

  useEffect(() => {
    if (member && !open) {
      setPerson(data.find((i) => i.slug.current === member));
      setOpen(true);
    }
  }, [member]);

  return (
    <div>
      <Meta path="/team" {...settings.seo} />

      <div className="bg-white mt-10">
        <div className="mx-auto px-4 max-w-7xl sm:px-6 lg:px-8">
          <div className="space-y-6">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
              {settings.leadership.title}
            </h2>
            <ul
              role="list"
              className="space-y-12 lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-8 lg:gap-y-12 lg:space-y-0"
            >
              {leaders.map((person) => (
                <li key={person.name}>
                  <div className="space-y-4 sm:grid sm:grid-cols-3 sm:gap-6 sm:space-y-0 lg:gap-8">
                    <div className="h-0 aspect-w-3 aspect-h-2 sm:aspect-w-3 sm:aspect-h-4 shadow-lg bg-gray-50 rounded-lg">
                      <Image
                        draggable={false}
                        className="object-cover shadow-lg rounded-lg"
                        alt=""
                        layout="fill"
                        priority
                        placeholder="blur"
                        blurDataURL={blurDataURL}
                        src={
                          isPageWide
                            ? imageBuilder(person.image)
                                .width(600)
                                .height(800)
                                .dpr(2)
                                .url()
                            : imageBuilder(person.image)
                                .width(600)
                                .height(400)
                                .dpr(2)
                                .url()
                        }
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <div className="space-y-4">
                        <div className="text-lg leading-6 font-medium space-y-1">
                          <h3>{person.name}</h3>
                          <p className="text-accent-600">{person.position}</p>
                        </div>

                        <div className="space-y-2">
                          <div className="text-lg">
                            <BlockContent
                              className="text-gray-500"
                              content={person.bio}
                            />
                          </div>
                          <ContactLinks
                            email={person.email}
                            linkedInUrl={person.linkedInUrl}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white">
        <div className="mx-auto py-12 px-4 max-w-7xl sm:px-6 lg:px-8 lg:py-24">
          <div className="space-y-12">
            <div className="space-y-5 sm:space-y-4">
              <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
                {settings.team.title}
              </h2>
              <p className="text-xl text-gray-500">{settings.team.subtitle}</p>

              <div>
                <div className="sm:hidden">
                  <label htmlFor="tabs" className="sr-only">
                    Select a tab
                  </label>
                  <select
                    id="tabs"
                    name="tabs"
                    onChange={(e) => {
                      const value = e.target.value;
                      router.push(
                        `/team${value ? `?department=${value}` : ""}`,
                        undefined,
                        {
                          scroll: false,
                          shallow: true,
                        }
                      );
                    }}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-accent-500 focus:border-accent-500 sm:text-sm rounded-md"
                    defaultValue={tabs.find((tab) => tab.current)?.name}
                  >
                    {tabs.map((tab) => (
                      <option value={tab.value || ""} key={tab.name}>
                        {tab.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="hidden sm:block">
                  <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                      {tabs.map((tab) => (
                        <Link
                          key={tab.name}
                          href={tab.href}
                          scroll={false}
                          shallow
                          className={classNames(
                            tab.current
                              ? "border-accent-500 text-accent-600"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                            "whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
                          )}
                          aria-current={tab.current ? "page" : undefined}
                        >
                          {tab.name}
                        </Link>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>
            </div>

            <ul
              role="list"
              className="space-y-12 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:gap-y-12 sm:space-y-0 lg:grid-cols-3 lg:gap-x-8"
            >
              {data.map((person) => (
                <li key={person.name}>
                  <div className="space-y-4">
                    <div
                      className="aspect-w-3 bg-gray-50 aspect-h-2 group cursor-pointer"
                      onClick={() => {
                        setPerson(person);
                        setOpen(true);
                      }}
                    >
                      <Image
                        draggable={false}
                        placeholder="blur"
                        blurDataURL={blurDataURL}
                        layout="fill"
                        className="object-cover shadow-lg rounded-lg sm:group-hover:opacity-75 transition-opacity"
                        src={imageBuilder(person.image)
                          .width(600)
                          .height(400)
                          .dpr(2)
                          .url()}
                        alt=""
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="text-lg leading-6 font-medium space-y-1">
                        <h3>{person.name}</h3>
                        <p className="text-accent-600">{person.position}</p>
                      </div>

                      <ContactLinks
                        email={person.email}
                        bio={person.bio}
                        linkedInUrl={person.linkedInUrl}
                        onClickBio={() => {
                          setPerson(person);
                          setOpen(true);
                        }}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Info modal */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed z-10 inset-0 overflow-y-auto"
          onClose={() => {
            if (member) {
              router.push("/team", undefined, {
                scroll: false,
                shallow: true,
              });
            }
            setOpen(false);
          }}
        >
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-top bg-white w-full rounded-lg pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div>
                  <div className="text-center sm:mt-0 sm:text-left">
                    <div className="aspect-w-3 aspect-h-2 mb-4">
                      <Image
                        draggable={false}
                        placeholder="blur"
                        blurDataURL={blurDataURL}
                        layout="fill"
                        className="object-cover rounded-t-lg"
                        src={
                          person.image
                            ? imageBuilder(person.image)
                                .width(900)
                                .height(600)
                                .dpr(2)
                                .url()
                            : null
                        }
                        alt=""
                      />
                    </div>

                    <div className="px-4 sm:px-6">
                      <div className="text-lg leading-6 font-medium space-y-1">
                        <h3>{person.name}</h3>
                        <p className="text-accent-600">{person.position}</p>
                      </div>

                      <div className="mt-2 mb-2">
                        <div className="text-sm text-gray-500">
                          <BlockContent
                            className="text-gray-500"
                            content={person.bio}
                          />
                        </div>
                      </div>

                      <div className="flex flex-row justify-center items-center sm:justify-start">
                        <ContactLinks
                          email={person.email}
                          linkedInUrl={person.linkedInUrl}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse px-4 sm:px-6">
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={() => {
                      if (member) {
                        router.push("/team", undefined, {
                          scroll: false,
                          shallow: true,
                        });
                      }
                      setOpen(false);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}

export async function getStaticProps({ preview = false }) {
  const data = await getAllTeamMemebers(preview);
  const settings = await getTeamPageSettings(preview);

  return {
    props: { data, settings },
    revalidate: 10,
  };
}
